import { useMemo, type ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '@course-authoring/shared-types';
import { useAuthorWithAiAvailability } from '../../api/authorWithAiQueries';
import { UNAVAILABLE_COPY } from '../../components/authorWithAi/authorWithAiCopy';
import { createAuthorWithAiStyles } from '../../components/authorWithAi/authorWithAiStyles';

export interface AuthorWithAiReadiness {
  ready: boolean;
  missingSettings: string[];
  /** False while the server has no private bucket for uploaded documents. */
  uploadsReady: boolean;
}

/** Renders the feature only when the server has it turned on; otherwise a plain "not available" page. */
export function AuthorWithAiGate({ children }: { children: (readiness: AuthorWithAiReadiness) => ReactNode }) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createAuthorWithAiStyles(tokens), [tokens]);
  const availability = useAuthorWithAiAvailability();
  if (availability.status === 'loading') {
    return (
      <div style={styles.centered} role="status" aria-label="Loading">
        <FontAwesomeIcon icon={faSpinner} spin aria-hidden="true" />
      </div>
    );
  }
  if (availability.status === 'unavailable') {
    return (
      <div style={styles.centered}>
        <h1 style={styles.heading}>{UNAVAILABLE_COPY.heading}</h1>
        <p style={styles.muted}>{UNAVAILABLE_COPY.body}</p>
      </div>
    );
  }
  return <>{children({ ready: availability.ready, missingSettings: availability.missingSettings, uploadsReady: availability.uploadsReady })}</>;
}
